import { NodeType } from '../../../core/workflow'
import { createUUID } from '../../../utils'
import { PaperEvents } from '../services/listeners'

/**
 * SVG path for the MUI WhatsApp icon.
 * Sourced from @mui/icons-material/WhatsApp.
 */
const WHATSAPP_SVG_PATH =
  'M16.75 13.96c.25.13.41.2.46.3.06.11.04.61-.21 1.18-.2.56-1.24 1.1-1.7 1.12-.46.02-.47.36-2.96-.73-2.49-1.09-3.99-3.75-4.11-3.92-.12-.17-.96-1.38-.92-2.61.05-1.22.69-1.8.95-2.04.24-.26.51-.29.68-.26h.47c.15 0 .36-.06.55.45l.69 1.87c.06.13.1.28.01.44l-.27.41-.39.42c-.12.12-.26.25-.12.5.12.26.62 1.09 1.32 1.78.91.88 1.71 1.17 1.95 1.3.24.14.39.12.54-.04l.81-.94c.19-.25.35-.19.58-.11l1.67.88M12 2a10 10 0 0 1 10 10 10 10 0 0 1-10 10c-1.97 0-3.8-.57-5.35-1.55L2 22l1.55-4.65A9.969 9.969 0 0 1 2 12 10 10 0 0 1 12 2m0 2a8 8 0 0 0-8 8c0 1.72.54 3.31 1.46 4.61L4.5 19.5l2.89-.96A7.95 7.95 0 0 0 12 20a8 8 0 0 0 8-8 8 8 0 0 0-8-8z'

const WHATSAPP_ICON_DATA_URI = `data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#fff"><path d="${WHATSAPP_SVG_PATH}"/></svg>`
)}`

/**
 * Enum for JointJS shapes.
 * @readonly
 * @enum {string}
 */
export const JointShapes = {
  Node: 'node',
  Link: 'link'
}

/**
 * Attributes for the node elements.
 * @typedef {Object} NodeAttrs
 * @property {{width: number; height: number}} nodeSize - The size of the node.
 * @property {{width: number; height: number}} label - The size of the label.
 * Label Width = node width (245) - icon width (60) - connection btn (25) - padding (30)
 * Label height = node height (100) - padding (20)
 *
 */
export const NodeAttrs = {
  nodeSize: { width: 245, height: 100 },
  label: { width: 100, height: 80, fontSize: 14 },
  description: { width: 160, height: 40, fontSize: 10 }
}

export const colorCode = {
  [NodeType.START]: '#4caf50',
  [NodeType.END]: '#d32f2f',
  [NodeType.EMAIL]: '#3f51b5',
  [NodeType.WHATSAPP]: '#3f51b5',
  [NodeType.SMS]: '#3f51b5',
  [NodeType.RCS]: '#3f51b5',
  [NodeType.UPDATE_ATTRIBUTE]: '#ffc107',
  [NodeType.TAG]: '#ffc107',
  [NodeType.CASE]: '#9c27b0',
  [NodeType.IF_ELSE]: '#9c27b0',
  [NodeType.WAIT_UNTIL]: '#ffab00',
  [NodeType.WAIT]: '#ffab00',
  [NodeType.CONDITION]: '#9c27b0',
  [NodeType.TEMPORARY]: '#795548',
  [NodeType.TIMER]: '#607d8b',
}

/**
 * Gets the attributes for a node.
 * @param {CanvasWorkflowNode} opt - The options for the node.
 * @returns {joint.dia.Element.Attributes} The attributes for the node.
 */
export const getNodeAttributes = opt => {
  return {
    id: opt.id,
    type: JointShapes.Node,
    position: opt.position,
    size: NodeAttrs.nodeSize,
    isTempNode: opt.type === NodeType.TEMPORARY, // marks the node as temporary
    preventHalo: opt.type === NodeType.TEMPORARY, // prevent halo on temporary node.
    attrs: {
      root: {
        class: `joint-node joint-node-${opt.type} ${opt.hasInboundPort ? '' : 'joint-node-hide-inport'} ${opt.hasOutboundPort ? '' : 'joint-node-hide-outport'}`,
        magnetSelector: 'outport'
      },
      inport: {
        magnet: `${opt.hasInboundPort ? 'passive' : false}`
      },
      label: {
        x: 'calc(w / 2 - 34)px',
        y: opt?.label?.length < 15 ? '40px' : '32px',
        textAnchor: 'start',
        // textVerticalAnchor: 'start',
        textWrap: {
          width: NodeAttrs.label.width,
          height: NodeAttrs.label.height,
          fontSize: NodeAttrs.label.fontSize,
          fontFamily: 'inherit',
          fontWeight: 700,
          ellipsis: true
        },
        event: opt.type === NodeType.TEMPORARY ? 'none' : 'element:label:pointerdown' // Disable label editing for temporary nodes
      },
      description: {
        x: '46px',
        y: '98px',
        textAnchor: 'start',
        // textVerticalAnchor: 'start',
        text: opt.description || '',
        textWrap: {
          width: NodeAttrs.description.width,
          height: NodeAttrs.description.height,
          fontSize: NodeAttrs.description.fontSize,
          fontFamily: 'inherit',
          fontWeight: 400,
          lineHeight: 1.2,
          color: '#cccccc',
          ellipsis: true
        },
        event: opt.type === NodeType.TEMPORARY ? 'none' : 'element:description:pointerdown' // Enable description editing for non-temporary nodes
      },
      outport: { magnet: true },
      connectionsBtn: {
        event: PaperEvents.ConnectionsBtnClick
      },
      errorIcon: {
        style: !opt?.validation?.isValid ? 'display: flex;' : 'display: none;'
      },
      successIcon: {
        style: opt?.validation?.isValid ? 'display: flex;' : 'display: none;'
      },
      chip: {
        html: opt.chip,
        event: PaperEvents.NodeChipClick,
        'data-tooltip': `Node index: ${opt.chip}`
      }
    }
  }
}

// Pre-define reusable markup objects to avoid recreation
const COMMON_MARKUP = {
  body: {
    tagName: 'rect',
    selector: 'body',
    className: 'joint-node-body'
  },
  inport: {
    tagName: 'circle',
    selector: 'inport',
    className: 'joint-node-inport'
  },
  label: {
    tagName: 'text',
    selector: 'label',
    className: 'joint-node-label',
    attributes: { y: '' }
  },
  divider: {
    tagName: 'line',
    selector: 'divider',
    className: 'joint-node-divider',
    attributes: {
      x1: '29px',
      y1: '76px',
      x2: '211px',
      y2: '76px'
    }
  },
  description: {
    tagName: 'text',
    selector: 'description',
    className: 'joint-node-description',
    attributes: { y: '' }
  },
  outport: {
    tagName: 'rect',
    selector: 'outport',
    className: 'joint-node-outport'
  },
  chip: {
    tagName: 'foreignObject',
    selector: 'chip',
    className: 'joint-node-chip',
    attributes: { 'data-tooltip': 'Node index: ' }
  }
}

/**
 * Gets the markup for a node.
 * @param {string} type - The node type.
 * @param {string} nodeIcon - The icon for the node - material icon name.
 * @returns {joint.dia.MarkupNodeJSON[]} The markup for the node.
 */
export const getNodeMarkup = (type, nodeIcon) => {
  const isEndNode = type === NodeType.END

  const isWhatsApp = type === NodeType.WHATSAPP

  // Create icon container with conditional children
  const iconChildren = isWhatsApp
    ? [
      {
        tagName: 'img',
        namespaceURI: 'http://www.w3.org/1999/xhtml',
        className: 'joint-node-custom-icon',
        attributes: {
          src: WHATSAPP_ICON_DATA_URI,
          alt: 'WhatsApp icon',
          style: 'width: 24px; height: 24px;'
        }
      }
    ]
    : nodeIcon.includes('/images/jb/')
      ? [
        {
          tagName: 'img',
          namespaceURI: 'http://www.w3.org/1999/xhtml',
          className: 'joint-node-custom-icon',
          attributes: {
            src: nodeIcon,
            alt: 'node icon',
          }
        }
      ]
      : [
        {
          tagName: 'span',
          namespaceURI: 'http://www.w3.org/1999/xhtml',
          className: 'material-symbols-outlined joint-node-icon',
          textContent: nodeIcon
        }
      ]

  const iconContainer = {
    selector: 'iconContainer',
    className: 'joint-node-icon-container',
    tagName: 'foreignObject',
    ref: 'iconBackground',
    children: iconChildren
  }

  // Build markup array efficiently using spread operator and conditional inclusion
  const markup = [
    COMMON_MARKUP.body,
    {
      tagName: 'rect',
      selector: 'iconBackground',
      className: 'joint-node-icon-bg',
      attributes: {
        style: `fill: ${colorCode[type] || 'var(--diagram-node-icon-bg-color)'};`
      },
    },
    iconContainer,
    COMMON_MARKUP.inport,
    COMMON_MARKUP.label,
    COMMON_MARKUP.chip,
    COMMON_MARKUP.divider,
    COMMON_MARKUP.description,
    // Conditionally include outport and connections button for non-end nodes
    ...(isEndNode
      ? []
      : [
        {
          tagName: 'g',
          selector: 'outportGroup',
          className: 'joint-node-outport-group',
          children: [
            {
              tagName: 'rect',
              selector: 'outport',
              className: 'joint-node-outport'
            },
            {
              tagName: 'foreignObject',
              selector: 'connectionsBtn',
              className: 'joint-node-connections-btn',
              children: [
                {
                  tagName: 'span',
                  namespaceURI: 'http://www.w3.org/1999/xhtml',
                  className: 'material-symbols-outlined joint-node-connections-icon',
                  textContent: 'add_circle'
                }
              ]
            }
          ]
        }
      ]),
    // Always include these elements
    {
      tagName: 'foreignObject',
      selector: 'errorIcon',
      className: 'joint-node-validation-icon-container',
      children: [
        {
          tagName: 'span',
          namespaceURI: 'http://www.w3.org/1999/xhtml',
          className: 'material-symbols-outlined joint-node-error-icon',
          textContent: 'error',
          attributes: {
            id: `error-icon-${createUUID()}`
          }
        }
      ]
    },
    {
      tagName: 'foreignObject',
      selector: 'successIcon',
      className: 'joint-node-validation-icon-container',
      children: [
        {
          tagName: 'span',
          namespaceURI: 'http://www.w3.org/1999/xhtml',
          className: 'material-symbols-outlined joint-node-success-icon',
          textContent: 'done_all',
        }
      ]
    }
  ]

  return markup
}

/**
 * Gets the markup for a  temp node.
 * @param {string} nodeIcon - The icon for the node - material icon name.
 * @returns {joint.dia.MarkupNodeJSON[]} The markup for the node.
 */
export const getTempNodeMarkup = () => [
  {
    tagName: 'rect',
    selector: 'body',
    className: 'joint-temp-node-body'
  },
  {
    tagName: 'circle',
    selector: 'iconBackground',
    className: 'joint-temp-node-icon-bg',
    attributes: {
      'data-tooltip': 'Temporary Node - Click to add a node',
    }
  },
  {
    selector: 'iconContainer',
    className: 'joint-temp-node-icon-container',
    tagName: 'foreignObject',
    ref: 'iconBackground',
    attributes: {
      'data-tooltip': 'Temporary Node - Click to add a node',
    },
    children: [
      {
        tagName: 'span',
        namespaceURI: 'http://www.w3.org/1999/xhtml',
        className: 'material-symbols-outlined joint-temp-node-connections-icon',
        textContent: 'add_circle',
        attributes: {
          'data-tooltip': 'Temporary Node - Click to add a node'
        }
      }
    ]
  },
  {
    tagName: 'circle',
    selector: 'inport',
    className: 'joint-node-inport'
  }
]
